import {
  REWARD_POOL_ACCESS_POLICY_TYPE,
  REWARD_POOL_DISTRIBUTION_POLICY,
  RewardPool,
  RewardPoolAccessPolicyTicket,
} from '@shared/api/types/rewardPools';

const getLimitTicketsPerUser = (rewardPool: RewardPool) => {
  if (rewardPool.distribution_policy !== REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE_V2) {
    return 0;
  }

  const ticketPolicy = rewardPool.access_policy.policies.find((policy) => {
    return policy.name === REWARD_POOL_ACCESS_POLICY_TYPE.TICKET;
  });

  if (!ticketPolicy) {
    return 0;
  }

  return (ticketPolicy as RewardPoolAccessPolicyTicket).options.limitPerUser;
};

export const checkLimitByTicketsReached = (rewardPool: RewardPool, entryAmount: number) => {
  const limitPerUser = getLimitTicketsPerUser(rewardPool);
  const totalLimit = rewardPool.entries_max || 0;
  const totalEntryAmount = rewardPool.entries_counter || 0;

  return entryAmount >= limitPerUser || totalEntryAmount >= totalLimit;
};

type AvailableTicketsForBalanceProps = {
  balance?: number;
  price?: number;
};

export const getAvailableTicketsForBalance = ({
  balance,
  price,
}: AvailableTicketsForBalanceProps) => {
  if (!balance || !price) {
    return 0;
  }

  return parseInt(String(balance / price));
};

type MaxAvailableTicketsProps = {
  price: number;
  balance: number;
  entryAmount: number;
  rewardPool: RewardPool;
};

export const calculateMaxAvailableTickets = ({
  price,
  balance,
  entryAmount,
  rewardPool,
}: MaxAvailableTicketsProps) => {
  if (
    !balance ||
    !price ||
    !rewardPool.entries_max ||
    typeof rewardPool?.entries_counter !== 'number'
  ) {
    return 0;
  }

  const limitTicketsPerUser = getLimitTicketsPerUser(rewardPool);
  const availablePerUser = limitTicketsPerUser - entryAmount;

  const availableTicketsTotal = rewardPool.entries_max - rewardPool.entries_counter;
  const availableForBalance = getAvailableTicketsForBalance({ balance, price });

  return Math.min(availablePerUser, availableTicketsTotal, availableForBalance);
};

type CheckIsInsufficientBalanceProps = {
  price: number;
  balance: number;
  entryAmount: number | string;
};

export const checkIsInsufficientBalance = ({
  price,
  balance,
  entryAmount,
}: CheckIsInsufficientBalanceProps): boolean => {
  return parseInt(String(entryAmount)) * price > balance;
};
